import { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Edit2, Check, AlertTriangle, Mail, Shield, Calendar, Car, ExternalLink, Fuel, Wrench, ShieldCheck, Clock, ChevronDown, ChevronUp, DollarSign, CreditCard, Gift, Crown, Infinity } from 'lucide-react'
import { getUserById, updateUser, getUserVehicles, getUserActivityTimeline, getUserActivitySummary, getUserFuelEntries, getUserRepairEntries, getUserInsurancePolicies, getUserSubscription, getUserPayments, grantUserSubscription } from '../services/userService.js'
import { useToast } from '../components/Toast.jsx'
import Pagination from '../components/Pagination.jsx'
import Modal from '../components/Modal.jsx'
import FormField from '../components/FormField.jsx'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import Breadcrumbs from '../components/Breadcrumbs.jsx'
import { statusColors, DEFAULT_PAGE_SIZE } from '../constants/statusColors.js'
import { validateUserForm, hasErrors } from '../utils/validation.js'
import { formatDate } from '../utils/dates.js'

const inputCls = 'w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'

const InfoItem = ({ icon: Icon, label, value, className = '' }) => (
  <div className="flex items-start gap-3">
    {Icon && <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />}
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`text-sm font-medium ${className}`}>{value || '-'}</p>
    </div>
  </div>
)

const StatCard = ({ icon: Icon, label, value, subValue, color = 'text-white' }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
    <div className="flex items-center gap-3 mb-2">
      {Icon && <Icon className={`w-5 h-5 ${color}`} />}
      <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
  </div>
)

const typeIcons = { FUEL: Fuel, REPAIR: Wrench, INSURANCE: ShieldCheck }
const typeColors = {
  FUEL: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  REPAIR: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  INSURANCE: 'bg-green-500/10 border-green-500/20 text-green-400',
}
const typeBadge = {
  FUEL: 'bg-amber-500/20 text-amber-400',
  REPAIR: 'bg-blue-500/20 text-blue-400',
  INSURANCE: 'bg-green-500/20 text-green-400',
}

const UserDetailPage = () => {
  const { userId } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [user, setUser] = useState(null)
  const [userLoading, setUserLoading] = useState(true)
  const [userError, setUserError] = useState(null)

  const [vehicles, setVehicles] = useState([])
  const [vPage, setVPage] = useState(0)
  const [vTotalPages, setVTotalPages] = useState(0)
  const [vTotalElements, setVTotalElements] = useState(0)
  const [vLoading, setVLoading] = useState(true)
  const [vError, setVError] = useState(null)

  const [editUser, setEditUser] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [formErrors, setFormErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [confirmClose, setConfirmClose] = useState(false)
  const [formDirty, setFormDirty] = useState(false)

  // Activity Timeline
  const [timeline, setTimeline] = useState([])
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)

  // Impersonation tabs
  const [activeTab, setActiveTab] = useState('timeline')
  const [fuelEntries, setFuelEntries] = useState([])
  const [fuelLoading, setFuelLoading] = useState(false)
  const [repairEntries, setRepairEntries] = useState([])
  const [repairLoading, setRepairLoading] = useState(false)
  const [insurancePolicies, setInsurancePolicies] = useState([])
  const [insuranceLoading, setInsuranceLoading] = useState(false)

  // Subscription
  const [subscription, setSubscription] = useState(null)
  const [subLoading, setSubLoading] = useState(true)
  const [subPayments, setSubPayments] = useState([])
  const [subPaymentsLoading, setSubPaymentsLoading] = useState(false)
  const [showPayments, setShowPayments] = useState(false)
  const [grantOpen, setGrantOpen] = useState(false)
  const [grantForm, setGrantForm] = useState({ planKey: 'PERSONAL_PRO', durationType: 'days', days: 90, customDate: '' })
  const [granting, setGranting] = useState(false)

  // Pricing V3 + Premium restoration: PERSONAL_PRO and PERSONAL_PREMIUM are grantable (Free = no entitlement row).
  // PERSONAL_BASIC is legacy: still displays in summaries but cannot be created.
  const PLAN_OPTIONS = ['PERSONAL_PRO', 'PERSONAL_PREMIUM']
  const LEGACY_PLAN_KEYS = new Set(['PERSONAL_BASIC'])
  const PLAN_VEHICLE_LIMITS = { PERSONAL_PRO: 5, PERSONAL_PREMIUM: 15 }
  const planLabel = (p) => {
    if (!p) return ''
    const raw = p.replace('PERSONAL_', '')
    const short = raw.charAt(0) + raw.slice(1).toLowerCase()
    return LEGACY_PLAN_KEYS.has(p) ? `${short} (legacy)` : short
  }
  const planOptionLabel = (p) => {
    const limit = PLAN_VEHICLE_LIMITS[p]
    return limit ? `${planLabel(p)} (${limit} vehicles)` : planLabel(p)
  }

  const fetchSubscription = useCallback(() => {
    setSubLoading(true)
    getUserSubscription(userId)
      .then(res => setSubscription(res.data))
      .catch(() => setSubscription(null))
      .finally(() => setSubLoading(false))
  }, [userId])

  const fetchPayments = useCallback(() => {
    setSubPaymentsLoading(true)
    getUserPayments(userId)
      .then(res => setSubPayments(res.data || []))
      .catch(() => setSubPayments([]))
      .finally(() => setSubPaymentsLoading(false))
  }, [userId])

  const handleGrant = async () => {
    setGranting(true)
    try {
      let validTo = null
      if (grantForm.durationType === 'lifetime') {
        validTo = '2099-12-31T23:59:59Z'
      } else if (grantForm.durationType === 'custom') {
        validTo = new Date(grantForm.customDate + 'T23:59:59Z').toISOString()
      } else {
        validTo = new Date(Date.now() + grantForm.days * 24 * 3600 * 1000).toISOString()
      }
      await grantUserSubscription(userId, { planKey: grantForm.planKey, validTo })
      toast.success(`Subscription granted successfully`)
      setGrantOpen(false)
      fetchSubscription()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to grant subscription')
    } finally {
      setGranting(false)
    }
  }

  const fetchUser = useCallback(() => {
    setUserLoading(true)
    setUserError(null)
    getUserById(userId)
      .then((res) => setUser(res.data))
      .catch(() => setUserError('Failed to load user details'))
      .finally(() => setUserLoading(false))
  }, [userId])

  const fetchVehicles = useCallback(() => {
    setVLoading(true)
    setVError(null)
    getUserVehicles(userId, vPage, DEFAULT_PAGE_SIZE)
      .then((res) => {
        const data = res.data
        setVehicles(data.content || [])
        setVTotalPages(data.totalPages || 0)
        setVTotalElements(data.totalElements || 0)
      })
      .catch(() => setVError('Failed to load vehicles'))
      .finally(() => setVLoading(false))
  }, [userId, vPage])

  const fetchTimeline = useCallback(() => {
    setTimelineLoading(true)
    getUserActivityTimeline(userId, 50)
      .then(res => setTimeline(res.data?.entries || []))
      .catch(() => setTimeline([]))
      .finally(() => setTimelineLoading(false))
  }, [userId])

  const fetchSummary = useCallback(() => {
    setSummaryLoading(true)
    getUserActivitySummary(userId)
      .then(res => setSummary(res.data))
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false))
  }, [userId])

  useEffect(() => { fetchUser() }, [fetchUser])
  useEffect(() => { fetchVehicles() }, [fetchVehicles])
  useEffect(() => { fetchTimeline(); fetchSummary() }, [fetchTimeline, fetchSummary])
  useEffect(() => { fetchSubscription() }, [fetchSubscription])

  // Tab data loading
  useEffect(() => {
    if (activeTab === 'fuel' && fuelEntries.length === 0 && !fuelLoading) {
      setFuelLoading(true)
      getUserFuelEntries(userId)
        .then(res => setFuelEntries(res.data || []))
        .catch(() => setFuelEntries([]))
        .finally(() => setFuelLoading(false))
    }
    if (activeTab === 'repairs' && repairEntries.length === 0 && !repairLoading) {
      setRepairLoading(true)
      getUserRepairEntries(userId)
        .then(res => setRepairEntries(res.data || []))
        .catch(() => setRepairEntries([]))
        .finally(() => setRepairLoading(false))
    }
    if (activeTab === 'insurance' && insurancePolicies.length === 0 && !insuranceLoading) {
      setInsuranceLoading(true)
      getUserInsurancePolicies(userId)
        .then(res => setInsurancePolicies(res.data || []))
        .catch(() => setInsurancePolicies([]))
        .finally(() => setInsuranceLoading(false))
    }
  }, [activeTab, userId])

  const openEdit = useCallback(() => {
    if (!user) return
    setEditUser(user)
    setEditForm({ name: user.name || '', email: user.email || '', emailVerified: user.emailVerified })
    setFormErrors({})
    setSaveError(null)
    setFormDirty(false)
  }, [user])

  const handleCloseModal = useCallback(() => {
    if (formDirty) setConfirmClose(true)
    else setEditUser(null)
  }, [formDirty])

  const updateField = useCallback((field, value) => {
    setEditForm(f => ({ ...f, [field]: value }))
    setFormDirty(true)
    setFormErrors(prev => ({ ...prev, [field]: undefined }))
  }, [])

  const saveEdit = async () => {
    const errors = validateUserForm(editForm)
    if (hasErrors(errors)) { setFormErrors(errors); return }
    if (!editUser) return
    setSaving(true)
    setSaveError(null)
    try {
      await updateUser(editUser.id, editForm)
      setEditUser(null)
      toast.success('User updated successfully')
      fetchUser()
    } catch {
      setSaveError('Failed to update user. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const breadcrumbs = [
    { label: 'Users', to: '/users' },
    { label: user?.name || user?.email || 'User Details' },
  ]

  if (userLoading) {
    return (
      <div className="space-y-4">
        <Breadcrumbs items={[{ label: 'Users', to: '/users' }, { label: 'Loading...' }]} />
        <div className="text-center py-20 text-gray-400">Loading user details...</div>
      </div>
    )
  }

  if (userError && !user) {
    return (
      <div className="space-y-4">
        <Breadcrumbs items={[{ label: 'Users', to: '/users' }, { label: 'Error' }]} />
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span>{userError}</span>
          <button onClick={fetchUser} className="ml-auto underline hover:no-underline">Retry</button>
        </div>
      </div>
    )
  }

  const formatAmount = (amount, currency) => {
    if (amount == null) return '-'
    return `${Number(amount).toFixed(2)} ${currency || 'GBP'}`
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={breadcrumbs} />

      {/* User Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
              {(user.name?.[0] || user.email?.[0] || '?').toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{user.name || 'No name'}</h1>
              <p className="text-sm text-gray-400">{user.email}</p>
            </div>
          </div>
          <button onClick={openEdit}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all text-sm">
            <Edit2 className="w-4 h-4" /> Edit
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <InfoItem icon={Mail} label="Email" value={user.email} />
          <InfoItem icon={Shield} label="Email Verified"
            value={user.emailVerified ? 'Verified' : 'Not Verified'}
            className={user.emailVerified ? 'text-green-400' : 'text-red-400'} />
          <InfoItem icon={Calendar} label="Created" value={formatDate(user.createdAt)} />
          <InfoItem icon={Car} label="Vehicles" value={vTotalElements} />
        </div>

        {user.auth0UserId && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-400">Auth0 ID</p>
            <p className="text-sm font-mono text-gray-500">{user.auth0UserId}</p>
          </div>
        )}
      </div>

      {/* Subscription Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-bold">Subscription</h2>
          </div>
          <button onClick={() => { setGrantOpen(true); setGrantForm({ planKey: 'PERSONAL_PRO', durationType: 'days', days: 90, customDate: '' }) }}
            className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all text-xs">
            <Gift className="w-3.5 h-3.5" /> {subscription ? 'Extend / Change' : 'Grant Pro'}
          </button>
        </div>

        {subLoading ? (
          <p className="text-sm text-gray-400">Loading subscription...</p>
        ) : subscription ? (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-400">Plan</p>
                <p className="text-sm font-semibold">{planLabel(subscription.planKey) || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Status</p>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                  subscription.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400' :
                  subscription.status === 'TRIALING' ? 'bg-blue-500/20 text-blue-400' :
                  subscription.status === 'CANCELED' ? 'bg-red-500/20 text-red-400' :
                  subscription.status === 'PAST_DUE' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>{subscription.status}</span>
              </div>
              <div>
                <p className="text-xs text-gray-400">Billing</p>
                <p className="text-sm">{subscription.billing || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Valid Until</p>
                <p className={`text-sm font-semibold ${subscription.validTo && new Date(subscription.validTo) < new Date() ? 'text-red-400' : subscription.validTo && new Date(subscription.validTo).getFullYear() >= 2099 ? 'text-amber-400' : ''}`}>
                  {subscription.validTo ? (new Date(subscription.validTo).getFullYear() >= 2099 ? 'Lifetime' : new Date(subscription.validTo).toLocaleDateString()) : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Type</p>
                <p className="text-sm">{subscription.purchaseType || '-'}</p>
              </div>
            </div>

            {subscription.stripeCustomerId && (
              <div className="flex items-center gap-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                <a href={`https://dashboard.stripe.com/customers/${subscription.stripeCustomerId}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300">
                  <ExternalLink className="w-3 h-3" /> Stripe Customer
                </a>
                {subscription.stripeSubscriptionId && (
                  <a href={`https://dashboard.stripe.com/subscriptions/${subscription.stripeSubscriptionId}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300">
                    <ExternalLink className="w-3 h-3" /> Stripe Subscription
                  </a>
                )}
              </div>
            )}

            {/* Payment History Toggle */}
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => { setShowPayments(!showPayments); if (!showPayments && subPayments.length === 0) fetchPayments() }}
                className="flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300 font-medium">
                <CreditCard className="w-3.5 h-3.5" />
                {showPayments ? 'Hide' : 'Show'} Payment History
                {showPayments ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {showPayments && (
                <div className="mt-3 space-y-2">
                  {subPaymentsLoading ? (
                    <p className="text-xs text-gray-400">Loading payments...</p>
                  ) : subPayments.length === 0 ? (
                    <p className="text-xs text-gray-400">No payment records</p>
                  ) : subPayments.map(p => (
                    <div key={p.paymentUuid} className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium">{Number(p.amount).toFixed(2)} {p.currency}</p>
                          <p className="text-xs text-gray-400">{p.description || `${p.planKey} ${p.billing}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.status === 'PAID' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                          {p.status}
                        </span>
                        <span className="text-xs text-gray-400">{p.occurredAt ? new Date(p.occurredAt).toLocaleDateString() : ''}</span>
                        {p.invoiceUrl && (
                          <a href={p.invoiceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-gray-400 mb-2">No active subscription</p>
            <p className="text-xs text-gray-500">Use "Grant Subscription" to give this user access</p>
          </div>
        )}
      </div>

      {/* Grant/Extend Subscription Modal */}
      <Modal open={grantOpen} title={subscription ? 'Extend / Change Subscription' : 'Grant Subscription'} onClose={() => setGrantOpen(false)} maxWidth="max-w-md" footer={
        <>
          <button onClick={() => setGrantOpen(false)} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Cancel</button>
          <button onClick={handleGrant} disabled={granting || (grantForm.durationType === 'custom' && !grantForm.customDate)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm">
            <Gift className="w-4 h-4" /> {granting ? 'Granting...' : 'Grant Access'}
          </button>
        </>
      }>
        <div className="space-y-4">
          <FormField label="Plan" htmlFor="grant-plan-ud">
            <select id="grant-plan-ud" value={grantForm.planKey} onChange={e => setGrantForm(f => ({ ...f, planKey: e.target.value }))}
              className={inputCls}>
              {PLAN_OPTIONS.map(p => <option key={p} value={p}>{planOptionLabel(p)}</option>)}
            </select>
          </FormField>

          <FormField label="Duration" htmlFor="grant-duration-ud">
            <div className="flex gap-2 mb-2">
              {[
                { key: 'days', label: 'Days' },
                { key: 'custom', label: 'Custom Date' },
                { key: 'lifetime', label: 'Lifetime', icon: Infinity },
              ].map(opt => (
                <button key={opt.key} type="button"
                  onClick={() => setGrantForm(f => ({ ...f, durationType: opt.key }))}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    grantForm.durationType === opt.key
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400 border border-transparent hover:border-gray-500'
                  }`}>
                  {opt.icon && <opt.icon className="w-3 h-3" />}
                  {opt.label}
                </button>
              ))}
            </div>

            {grantForm.durationType === 'days' && (
              <input id="grant-duration-ud" type="number" min="1" max="3650"
                value={grantForm.days} onChange={e => setGrantForm(f => ({ ...f, days: Number(e.target.value) }))}
                className={inputCls} placeholder="Number of days" />
            )}
            {grantForm.durationType === 'custom' && (
              <input id="grant-duration-ud" type="date"
                value={grantForm.customDate} onChange={e => setGrantForm(f => ({ ...f, customDate: e.target.value }))}
                className={inputCls} min={new Date().toISOString().split('T')[0]} />
            )}
            {grantForm.durationType === 'lifetime' && (
              <p className="text-xs text-amber-400 flex items-center gap-1.5 mt-1">
                <Infinity className="w-4 h-4" /> Subscription will be valid until 2099-12-31
              </p>
            )}
          </FormField>

          {subscription && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl text-sm text-blue-400">
              Current plan: <strong>{planLabel(subscription.planKey)}</strong> &bull;
              Status: <strong>{subscription.status}</strong> &bull;
              Valid until: <strong>{subscription.validTo ? (new Date(subscription.validTo).getFullYear() >= 2099 ? 'Lifetime' : new Date(subscription.validTo).toLocaleDateString()) : '-'}</strong>
            </div>
          )}
        </div>
      </Modal>

      {/* Activity Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard icon={Fuel} label="Fuel Entries" value={summary.fuelEntries}
            subValue={`${Number(summary.totalFuelSpend || 0).toFixed(2)} total`} color="text-amber-400" />
          <StatCard icon={Wrench} label="Repairs" value={summary.repairEntries}
            subValue={`${Number(summary.totalRepairSpend || 0).toFixed(2)} total`} color="text-blue-400" />
          <StatCard icon={ShieldCheck} label="Insurance" value={summary.insurancePolicies}
            subValue={`${Number(summary.totalInsuranceSpend || 0).toFixed(2)} total`} color="text-green-400" />
          <StatCard icon={Car} label="Vehicles" value={vTotalElements} color="text-purple-400" />
          <StatCard icon={DollarSign} label="Total Spend" color="text-emerald-400"
            value={Number((summary.totalFuelSpend || 0) + (summary.totalRepairSpend || 0) + (summary.totalInsuranceSpend || 0)).toFixed(2)} />
        </div>
      )}

      {/* User Vehicles */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Vehicles ({vTotalElements})</h2>
        </div>

        {vError && (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm mb-4">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <span>{vError}</span>
            <button onClick={fetchVehicles} className="ml-auto underline hover:no-underline">Retry</button>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Plate</th>
                <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Brand / Model</th>
                <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Year</th>
                <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Fuel</th>
                <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                <th scope="col" className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Mileage</th>
                <th scope="col" className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {vLoading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading...</td></tr>
              ) : vehicles.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">This user has no vehicles</td></tr>
              ) : (
                vehicles.map((v) => (
                  <tr key={v.vehicleUuid} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <Link to={`/vehicles/${v.vehicleUuid}`} className="font-mono font-semibold text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        {v.plate}
                      </Link>
                      <span className="text-xs text-gray-400 ml-1">{v.plateCountry}</span>
                    </td>
                    <td className="px-6 py-4 text-sm">{[v.brandName, v.modelName].filter(Boolean).join(' ') || '-'}</td>
                    <td className="px-6 py-4 text-center text-sm">{v.productionYear || '-'}</td>
                    <td className="px-6 py-4 text-center text-xs">{v.fuelType || '-'}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[v.status] || 'bg-gray-500/20 text-gray-400'}`}>{v.status}</span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm">{v.mileage != null ? v.mileage.toLocaleString() + ' km' : '-'}</td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`/vehicles/${v.vehicleUuid}`} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors inline-flex" aria-label={`View vehicle ${v.plate}`}>
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination page={vPage} totalPages={vTotalPages} totalElements={vTotalElements} onPageChange={setVPage} label="vehicles" />
      </div>

      {/* User Impersonation / Debug View */}
      <div>
        <h2 className="text-lg font-bold mb-4">User Data (Impersonation / Debug)</h2>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
          {[
            { key: 'timeline', label: 'Activity Timeline', icon: Clock },
            { key: 'fuel', label: 'Fuel Entries', icon: Fuel },
            { key: 'repairs', label: 'Repairs', icon: Wrench },
            { key: 'insurance', label: 'Insurance', icon: ShieldCheck },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-500'
                  : 'text-gray-400 hover:text-white'
              }`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            {timelineLoading ? (
              <p className="text-center py-8 text-gray-400">Loading timeline...</p>
            ) : timeline.length === 0 ? (
              <p className="text-center py-8 text-gray-400">No activity recorded for this user</p>
            ) : (
              <div className="space-y-3">
                {timeline.map((entry, idx) => {
                  const Icon = typeIcons[entry.type] || Clock
                  return (
                    <div key={`${entry.type}-${entry.id}-${idx}`}
                      className={`flex items-center gap-4 p-3 rounded-xl border ${typeColors[entry.type] || 'bg-gray-500/10 border-gray-500/20'}`}>
                      <Icon className="w-5 h-5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${typeBadge[entry.type]}`}>
                            {entry.type}
                          </span>
                          <span className="text-sm font-medium truncate">{entry.description}</span>
                        </div>
                        {entry.vehicleUuid && (
                          <Link to={`/vehicles/${entry.vehicleUuid}`}
                            className="text-xs text-gray-400 hover:text-blue-400 font-mono mt-0.5 block">
                            Vehicle: {entry.vehicleUuid.substring(0, 8)}...
                          </Link>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        {entry.amount != null && (
                          <p className="text-sm font-semibold">{formatAmount(entry.amount, entry.currency)}</p>
                        )}
                        <p className="text-xs text-gray-400">
                          {entry.occurredAt ? new Date(entry.occurredAt).toLocaleDateString() : '-'}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Fuel Tab */}
        {activeTab === 'fuel' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Date</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Type</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Quantity</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Unit Price</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Total</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Odometer</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Location</th>
                </tr>
              </thead>
              <tbody>
                {fuelLoading ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading...</td></tr>
                ) : fuelEntries.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">No fuel entries</td></tr>
                ) : fuelEntries.map(f => (
                  <tr key={f.entryUuid} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-6 py-3 text-sm">{f.occurredAt ? new Date(f.occurredAt).toLocaleDateString() : '-'}</td>
                    <td className="px-6 py-3 text-sm">
                      {f.energyType === 'ELECTRIC' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-medium">
                          {f.chargeType ? f.chargeType.replace(/_/g, ' ') : 'ELECTRIC'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500 text-xs font-medium">
                          {f.fuelType || 'FUEL'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-right">
                      {f.energyType === 'ELECTRIC'
                        ? (f.energyKwh != null ? `${Number(f.energyKwh).toFixed(2)} kWh` : (f.quantity != null ? `${Number(f.quantity).toFixed(2)} kWh` : '-'))
                        : (f.quantity != null ? `${Number(f.quantity).toFixed(2)} L` : '-')
                      }
                    </td>
                    <td className="px-6 py-3 text-sm text-right">{f.unitPrice != null ? `${Number(f.unitPrice).toFixed(4)}/${f.energyType === 'ELECTRIC' ? 'kWh' : 'L'}` : '-'}</td>
                    <td className="px-6 py-3 text-sm text-right font-semibold">{formatAmount(f.totalCost, f.currency)}</td>
                    <td className="px-6 py-3 text-sm text-right">{f.odometer != null ? Number(f.odometer).toLocaleString() : '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-400 truncate max-w-[200px]">{f.locationLabel || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Repairs Tab */}
        {activeTab === 'repairs' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Date</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Title</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Shop</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Total Cost</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Odometer</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Note</th>
                </tr>
              </thead>
              <tbody>
                {repairLoading ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-400">Loading...</td></tr>
                ) : repairEntries.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-400">No repair entries</td></tr>
                ) : repairEntries.map(r => (
                  <tr key={r.repairUuid} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-6 py-3 text-sm">{r.occurredAt ? new Date(r.occurredAt).toLocaleDateString() : '-'}</td>
                    <td className="px-6 py-3 text-sm font-medium">{r.title || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-400">{r.shopName || '-'}</td>
                    <td className="px-6 py-3 text-sm text-right font-semibold">{formatAmount(r.totalCost, r.currency)}</td>
                    <td className="px-6 py-3 text-sm text-right">{r.odometer != null ? Number(r.odometer).toLocaleString() : '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-400 truncate max-w-[200px]">{r.note || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Insurance Tab */}
        {activeTab === 'insurance' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Policy #</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Provider</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Type</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Valid From</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Valid Until</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Amount</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Reg</th>
                </tr>
              </thead>
              <tbody>
                {insuranceLoading ? (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading...</td></tr>
                ) : insurancePolicies.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-400">No insurance policies</td></tr>
                ) : insurancePolicies.map(ins => {
                  const isExpired = ins.validUntil && new Date(ins.validUntil) < new Date()
                  return (
                    <tr key={ins.insuranceUuid} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-6 py-3 text-sm font-mono">{ins.policyNumber || '-'}</td>
                      <td className="px-6 py-3 text-sm">{ins.providerName || '-'}</td>
                      <td className="px-6 py-3 text-center text-xs">{ins.coverType || '-'}</td>
                      <td className="px-6 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          ins.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400' :
                          ins.status === 'EXPIRED' ? 'bg-red-500/20 text-red-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>{ins.status}</span>
                      </td>
                      <td className="px-6 py-3 text-sm">{ins.validFrom ? new Date(ins.validFrom).toLocaleDateString() : '-'}</td>
                      <td className={`px-6 py-3 text-sm ${isExpired ? 'text-red-400' : ''}`}>
                        {ins.validUntil ? new Date(ins.validUntil).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-3 text-sm text-right font-semibold">{formatAmount(ins.amount, ins.currency)}</td>
                      <td className="px-6 py-3 text-sm font-mono text-gray-400">{ins.vehicleRegNumber || '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      <Modal open={!!editUser} title="Edit User" onClose={handleCloseModal} footer={
        <>
          <button onClick={handleCloseModal} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Cancel</button>
          <button onClick={saveEdit} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm">
            <Check className="w-4 h-4" />{saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }>
        {saveError && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">{saveError}</div>
        )}
        <FormField label="Name" htmlFor="edit-ud-name" error={formErrors.name}>
          <input id="edit-ud-name" type="text" value={editForm.name || ''} onChange={(e) => updateField('name', e.target.value)} className={inputCls} />
        </FormField>
        <FormField label="Email" htmlFor="edit-ud-email" error={formErrors.email}>
          <input id="edit-ud-email" type="email" value={editForm.email || ''} onChange={(e) => updateField('email', e.target.value)} className={inputCls} />
        </FormField>
        <div className="flex items-center gap-3">
          <label htmlFor="edit-ud-verified" className="text-sm font-medium text-gray-500 dark:text-gray-400">Email Verified</label>
          <button id="edit-ud-verified" role="switch" aria-checked={editForm.emailVerified}
            onClick={() => updateField('emailVerified', !editForm.emailVerified)}
            className={`w-10 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editForm.emailVerified ? 'bg-green-500' : 'bg-gray-600'}`}>
            <div className={`w-4 h-4 bg-white rounded-full transition-transform ml-1 ${editForm.emailVerified ? 'translate-x-4' : ''}`} />
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmClose}
        title="Unsaved changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmLabel="Discard"
        onConfirm={() => { setConfirmClose(false); setEditUser(null) }}
        onCancel={() => setConfirmClose(false)}
      />
    </div>
  )
}

export default UserDetailPage
